/**
 * Sovereign Ash: Nareth Protocol
 * Settings Persistence Module
 *
 * Handles loading, saving, and migrating user settings
 * with versioned schema support.
 */

import { z } from 'zod';

// ============================================================================
// Settings Schema
// ============================================================================

export const GraphicsSettingsSchema = z.object({
  preset: z.enum(['ultra', 'high', 'medium', 'low', 'custom']).default('high'),
  resolution: z.number().min(50).max(100).default(100),
  frameCap: z.union([z.literal(30), z.literal(60), z.literal(120), z.literal(0)]).default(60),
  shadows: z.enum(['off', 'low', 'medium', 'high', 'ultra']).default('high'),
  reflections: z.boolean().default(true),
  volumetrics: z.boolean().default(false),
  antialiasing: z.enum(['off', 'fxaa', 'taa']).default('taa'),
  motionBlur: z.boolean().default(false),
  anisotropy: z.union([z.literal(1), z.literal(2), z.literal(4), z.literal(8), z.literal(16)]).default(8),
});

export const AudioSettingsSchema = z.object({
  master: z.number().min(0).max(100).default(80),
  music: z.number().min(0).max(100).default(70),
  dialogue: z.number().min(0).max(100).default(90),
  sfx: z.number().min(0).max(100).default(80),
  dynamicRange: z.enum(['night', 'balanced', 'cinema']).default('balanced'),
  subtitles: z.boolean().default(true),
  dialogueBoost: z.number().min(0).max(9).default(0),
  tinnitusSafe: z.boolean().default(false),
  reducedHFAlarms: z.boolean().default(false),
});

export const ControlSettingsSchema = z.object({
  sensitivity: z.number().min(0.1).max(10).default(1),
  invertY: z.boolean().default(false),
  deadzone: z.number().min(0).max(0.5).default(0.1),
  throttleMode: z.enum(['hold', 'toggle']).default('hold'),
  torsoCentering: z.boolean().default(true),
  aimAssist: z.number().min(0).max(100).default(50),
  headLookMode: z.enum(['hold', 'toggle']).default('hold'),
  freeLookMode: z.enum(['hold', 'toggle']).default('hold'),
});

export const AccessibilitySettingsSchema = z.object({
  hudScale: z.number().min(0.8).max(1.5).default(1),
  colorblindMode: z.enum(['off', 'protanopia', 'deuteranopia', 'tritanopia']).default('off'),
  reducedShake: z.boolean().default(false),
  reducedFlashes: z.boolean().default(false),
  horizonStabilization: z.boolean().default(true),
  fov: z.number().min(60).max(120).default(90),
  subtitleScale: z.number().min(0.8).max(1.5).default(1),
  showSpeakerLabels: z.boolean().default(true),
  showDirectionIndicators: z.boolean().default(true),
  showNonSpeechCaptions: z.boolean().default(true),
});

export const SettingsSchema = z.object({
  version: z.number().int().positive().default(1),
  graphics: GraphicsSettingsSchema.default({}),
  audio: AudioSettingsSchema.default({}),
  controls: ControlSettingsSchema.default({}),
  accessibility: AccessibilitySettingsSchema.default({}),
});

export type GraphicsSettings = z.infer<typeof GraphicsSettingsSchema>;
export type AudioSettings = z.infer<typeof AudioSettingsSchema>;
export type ControlSettings = z.infer<typeof ControlSettingsSchema>;
export type AccessibilitySettings = z.infer<typeof AccessibilitySettingsSchema>;
export type Settings = z.infer<typeof SettingsSchema>;

// ============================================================================
// Storage Keys and Versioning
// ============================================================================

const STORAGE_KEY = 'sovereign_ash_settings';
const CURRENT_VERSION = 1;

// ============================================================================
// Settings Manager
// ============================================================================

export class SettingsManager {
  private settings: Settings;
  private listeners: Set<(settings: Settings) => void> = new Set();

  constructor() {
    this.settings = this.load();
  }

  /**
   * Load settings from localStorage with migration support.
   */
  private load(): Settings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return this.getDefaults();
      }

      const parsed = JSON.parse(raw);
      const migrated = this.migrate(parsed);
      const validated = SettingsSchema.parse(migrated);
      return validated;
    } catch (error) {
      console.warn('Failed to load settings, using defaults:', error);
      return this.getDefaults();
    }
  }

  /**
   * Get default settings.
   */
  getDefaults(): Settings {
    return SettingsSchema.parse({ version: CURRENT_VERSION });
  }

  /**
   * Migrate settings from older versions.
   */
  private migrate(data: unknown): unknown {
    if (typeof data !== 'object' || data === null) {
      return this.getDefaults();
    }

    const obj = data as Record<string, unknown>;
    let version = (obj.version as number) || 0;

    // Migration chain
    // Version 0 → 1: Initial schema
    if (version < 1) {
      // Add any migration logic here for future versions
      version = 1;
    }

    // Future migrations:
    // if (version < 2) { ... version = 2; }

    return { ...obj, version: CURRENT_VERSION };
  }

  /**
   * Save settings to localStorage.
   */
  save(): void {
    try {
      const serialized = JSON.stringify(this.settings);
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }

  /**
   * Get current settings (immutable copy).
   */
  get(): Readonly<Settings> {
    return { ...this.settings };
  }

  /**
   * Get a specific settings section.
   */
  getSection<K extends keyof Omit<Settings, 'version'>>(section: K): Readonly<Settings[K]> {
    return { ...this.settings[section] };
  }

  /**
   * Update graphics settings.
   */
  updateGraphics(partial: Partial<GraphicsSettings>): void {
    this.settings.graphics = { ...this.settings.graphics, ...partial };
    this.notifyAndSave();
  }

  /**
   * Update audio settings.
   */
  updateAudio(partial: Partial<AudioSettings>): void {
    this.settings.audio = { ...this.settings.audio, ...partial };
    this.notifyAndSave();
  }

  /**
   * Update control settings.
   */
  updateControls(partial: Partial<ControlSettings>): void {
    this.settings.controls = { ...this.settings.controls, ...partial };
    this.notifyAndSave();
  }

  /**
   * Update accessibility settings.
   */
  updateAccessibility(partial: Partial<AccessibilitySettings>): void {
    this.settings.accessibility = { ...this.settings.accessibility, ...partial };
    this.notifyAndSave();
  }

  /**
   * Reset all settings to defaults.
   */
  reset(): void {
    this.settings = this.getDefaults();
    this.notifyAndSave();
  }

  /**
   * Reset a specific section to defaults.
   */
  resetSection(section: keyof Omit<Settings, 'version'>): void {
    const defaults = this.getDefaults();
    // Type assertion needed for generic key assignment
    (this.settings as Record<string, unknown>)[section] = defaults[section];
    this.notifyAndSave();
  }

  /**
   * Subscribe to settings changes.
   */
  subscribe(listener: (settings: Settings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify listeners and save.
   */
  private notifyAndSave(): void {
    this.save();
    const snapshot = this.get();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  /**
   * Export settings as JSON string.
   */
  export(): string {
    return JSON.stringify(this.settings, null, 2);
  }

  /**
   * Import settings from JSON string.
   */
  import(json: string): boolean {
    try {
      const parsed = JSON.parse(json);
      const validated = SettingsSchema.parse(parsed);
      this.settings = validated;
      this.notifyAndSave();
      return true;
    } catch (error) {
      console.error('Failed to import settings:', error);
      return false;
    }
  }
}

// Singleton instance
let settingsInstance: SettingsManager | null = null;

/**
 * Get the global settings manager instance.
 */
export function getSettingsManager(): SettingsManager {
  if (!settingsInstance) {
    settingsInstance = new SettingsManager();
  }
  return settingsInstance;
}
